/**
 * BoosterV2 New Home - Lista planów i tworzenie nowego
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { 
  Plus, Rocket, Clock, CheckCircle, Play, Pause, 
  Loader2, ChevronRight, Sparkles, Calendar, Trash2
} from 'lucide-react';
import UpgradePrompt from '@/components/UpgradePrompt';

import { API_BASE as API } from '@/utils/api';

const STATUS_CONFIG = {
  draft: { label: 'Szkic', color: 'bg-gray-100 text-gray-600', icon: Clock },
  ready: { label: 'Gotowy', color: 'bg-orange-100 text-orange-700', icon: Sparkles },
  active: { label: 'Aktywny', color: 'bg-green-100 text-green-700', icon: Play },
  running: { label: 'Aktywny', color: 'bg-green-100 text-green-700', icon: Play },
  paused: { label: 'Wstrzymany', color: 'bg-amber-100 text-amber-700', icon: Pause },
  completed: { label: 'Zakończony', color: 'bg-blue-100 text-blue-700', icon: CheckCircle }
};

function PlanCard({ plan, onClick, onDelete, onPause, deleting, pausing }) {
  const status = STATUS_CONFIG[plan.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  
  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Czy na pewno chcesz usunąć kampanię "${plan.business_name || 'Nowy plan'}"?`)) {
      onDelete(plan.id);
    }
  };
  
  const handlePause = (e) => {
    e.stopPropagation();
    onPause(plan.id, plan.status);
  };
  
  return (
    <div className="relative">
      <div
        onClick={onClick}
        className="w-full bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-orange-300 hover:shadow-md transition-all group cursor-pointer"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                {plan.business_name || 'Nowy plan'}
              </h3>
              <p className="text-sm text-gray-500">
                {plan.goal_type ? `Cel: ${plan.goal_type}` : 'Brak ustawionego celu'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {status.label}
            </span>
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{plan.posts_count || 0} postów</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4" />
            <span>{plan.published_count || 0} opublikowanych</span>
          </div>
        </div>
        
        {/* Arrow */}
        <div className="mt-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-campaign-${plan.id}`}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              <span className="ml-1">Usuń</span>
            </Button>
            {(plan.status === 'ready' || plan.status === 'running' || plan.status === 'scheduled' || plan.status === 'paused') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePause}
                disabled={pausing}
                className={plan.status === 'paused' 
                  ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                  : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                }
                data-testid={`pause-campaign-${plan.id}`}
              >
                {pausing ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                  plan.status === 'paused' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />
                }
                <span className="ml-1">{plan.status === 'paused' ? 'Wznów' : 'Wstrzymaj'}</span>
              </Button>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
        </div>
      </div>
    </div>
  );
}

export default function BoosterV2NewHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [pausingId, setPausingId] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  
  useEffect(() => {
    fetchPlans();
    fetchBillingStatus();
  }, [token]);

  const fetchBillingStatus = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/billing/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setBillingStatus(await res.json());
    } catch (e) {
      // on error, allow access
      setBillingStatus({ can_access: true });
    }
  };
  
  const fetchPlans = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPlans(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (planId) => {
    setDeleting(planId);
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns/${planId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Kampania usunięta');
        setPlans(plans.filter(p => p.id !== planId));
      } else {
        toast.error('Nie udało się usunąć kampanii');
      }
    } catch (err) {
      toast.error('Błąd podczas usuwania');
    } finally {
      setDeleting(null);
    }
  };
  
  const handlePause = async (planId, currentStatus) => {
    const isPaused = currentStatus === 'paused';
    const endpoint = isPaused ? 'resume' : 'pause';
    
    setPausingId(planId);
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns/${planId}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlans(plans.map(p => p.id === planId ? { ...p, status: data.status } : p));
        toast.success(isPaused ? 'Kampania wznowiona!' : 'Kampania wstrzymana');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Nie udało się zmienić statusu');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setPausingId(null);
    }
  };
  
  const handleCreate = async () => {
    if (billingStatus && !billingStatus.can_access) {
      setShowUpgrade(true);
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (res.ok) {
        const data = await res.json();
        navigate(`/panel/booster-v2-new/wizard/${data.id}`);
      } else {
        toast.error('Nie udało się utworzyć planu');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setCreating(false);
    }
  };
  
  const handlePlanClick = (plan) => {
    if (plan.status === 'draft') {
      navigate(`/panel/booster-v2-new/wizard/${plan.id}`);
    } else {
      navigate(`/panel/booster-v2-new/plan/${plan.id}`);
    }
  };
  
  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Booster V2</h1>
            <p className="text-gray-600 mt-1">
              Tworzenie i zarządzanie planami promocji
            </p>
          </div>
          
          <Button
            onClick={handleCreate}
            disabled={creating}
            className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Nowy plan
          </Button>
        </div>
        
        {/* Plans list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-orange-100 to-purple-100 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Jeszcze nie masz żadnego planu
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Stwórz swój pierwszy plan promocji. AI pomoże Ci wygenerować treści 
              dopasowane do Twojej firmy.
            </p>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Stwórz pierwszy plan
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {plans.map((plan) => (
              <PlanCard 
                key={plan.id} 
                plan={plan} 
                onClick={() => handlePlanClick(plan)}
                onDelete={handleDelete}
                onPause={handlePause}
                deleting={deleting === plan.id}
                pausing={pausingId === plan.id}
              />
            ))}
          </div>
        )}
      </div>
      {showUpgrade && <UpgradePrompt feature="Booster AI" onClose={() => setShowUpgrade(false)} />}
    </PanelLayout>
  );
}
